import * as _ from 'underscore';
import { ConnectionSection } from './connectionSection';
import { Templates } from '../../compile';
import { ID } from '../../const';
import { Size } from 'mappMvc/const';
import { View } from 'mappMvc/models/views';
import { BaseForm } from 'mappMvc/models/ui/forms';
import { Radio, Input, NotificationElement, NumberInput } from 'mappMvc/models/ui/uiElements';
import { IOption } from 'mappMvc/interfaces';
import { DbProviderType } from 'mappClient/enums/core';
import { Notification, Util } from 'mappMvc/models';
import { ConnectionContentClient, ManagementClient } from 'mappClient/clients';
import { ViewOptions } from 'mappMvc/interfaces/options';
import { IValidationResult, IValidateConnection, ISaveConnection } from 'mappClient/models';
import { NotificationType } from 'mappMvc/enums';
import { BaseConnectionClient } from '../interface';
import { loading, notify } from 'mappMvc/decorators';
import { Localization } from 'mappMvc/compile';

export class NewConnection extends View {
	protected form: BaseForm;
	protected initialCatalog: KnockoutObservable<string>;
	protected password: KnockoutObservable<string>;
	public provider: Radio<IOption<DbProviderType>>;
	protected dataSource: KnockoutObservable<string>;
	protected userId: KnockoutObservable<string>;
	protected port: KnockoutObservable<number>;

	private dataSourceInput: Input<string>;
	private databaseInput: Input<string>;
	private portInput: NumberInput;
	private databaseActive: KnockoutObservable<boolean>;
	private portActive: KnockoutObservable<boolean>;
	public notification: Notification;
	public client: ConnectionContentClient | ManagementClient;
	public get getForm(): BaseForm {
		return this.form;
	}

	constructor(client: ConnectionContentClient | ManagementClient, options: ViewOptions) {
		super(
			_.defaults(options, <ViewOptions>{
				viewId: ID.New
			})
		);

		this.client = client;
		this.notification = new Notification();

		//#region FORM
		this.form = new BaseForm();

		this.form.addUIElement(new NotificationElement('notification', null, this.notification));

		var providerItems: IOption<DbProviderType>[] = [
			<IOption<DbProviderType>>{
				id: DbProviderType.Oracle,
				label: Localization.Studio.Connection.Oracle
			},
			<IOption<DbProviderType>>{
				id: DbProviderType.SqlServer,
				label: Localization.Studio.Connection.SqlServer
			},
			<IOption<DbProviderType>>{
				id: DbProviderType.PostgreSql,
				label: Localization.Studio.Connection.PostgreSQL
			},
			<IOption<DbProviderType>>{
				id: DbProviderType.Hana,
				label: Localization.Studio.Connection.Hana
			}
		];
		this.provider = new Radio(
			ID.Provider,
			Localization.Studio.Connection.Provider,
			null,
			providerItems,
			{
				rules: [{ rule: 'required', params: true }]
			}
		);
		this.provider.value.subscribe(data => {
			if (data) {
				this.showConnectionItems(data);
			}
		});
		this.form.addUIElement(this.provider);

		this.dataSource = ko.observable<string>();
		this.dataSourceInput = new Input(
			ID.Datasource,
			Localization.Studio.Connection.Datasource,
			this.dataSource,
			{
				rules: [{ rule: 'required', params: true }]
			}
		);
		this.form.addUIElement(this.dataSourceInput);

		this.portActive = ko.observable<boolean>(false);
		this.port = ko.observable<number>();
		this.portInput = new NumberInput(ID.Port, Localization.Studio.Connection.Port, this.port, {
			size: Size._1_8,
			visible: this.portActive,
			grouping: false
		});
		this.form.addUIElement(this.portInput);

		this.databaseActive = ko.observable<boolean>(false);
		this.initialCatalog = ko.observable<string>();
		this.databaseInput = new Input(
			ID.Database,
			Localization.Studio.Connection.Database,
			this.initialCatalog,
			{
				size: Size._1_2,
				visible: this.databaseActive,
				rules: [{ rule: 'required', params: this.databaseActive }]
			}
		);
		this.form.addUIElement(this.databaseInput);

		this.userId = ko.observable<string>();
		var userIdInput = new Input(ID.User, Localization.Studio.User.User, this.userId, {
			size: Size._1_2,
			rules: [{ rule: 'required', params: true }]
		});
		this.form.addUIElement(userIdInput);

		this.password = ko.observable<string>();
		var passwordInput = new Input(ID.Password, Localization.Login.Password, this.password, {
			size: Size._1_2,
			widget: {
				widget: Templates.widgets_password
			},
			rules: [{ rule: 'required', params: true }]
		});
		this.form.addUIElement(passwordInput);
		//#endregion

		this.form.ClickedSubmit.subscribe(() => {
			this.submit();
		});
	}

	//overwrite
	public activated(routeSections: string[]): void {
		this.reset();
	}

	public validate(id?: string): Promise<any> {
		let deferred = $.Deferred<IValidationResult>();
		this.notification.notify(Localization.Progress.Validating, NotificationType.Hint);
		this.notification.show();
		(<BaseConnectionClient>this.client).isValidConnection(this.serializeValidateConnection()).then(
			(validationResult: IValidationResult) => {
				if (validationResult.isValid) {
					this.notification.setText(
						Localization.Progress.Validating.concat(
							' ',
							Localization.Validation.Ok,
							' ',
							Localization.Progress.Saving
						)
					);
					deferred.resolve(validationResult);
				} else {
					this.notification.notify(validationResult.message, NotificationType.Error);
					deferred.reject(validationResult);
				}
			},
			() => {
				this.notification.notify(Localization.Errors.DatabaseNoConnection, NotificationType.Error);
				deferred.reject();
			},
			deferred.progress
		);
		return deferred.promise();
	}

	@loading
	@notify(() => Localization.Saved)
	public submit(): Promise<any> {
		return this.validate()
			.then(() => (<BaseConnectionClient>this.client).saveConnection(this.serialize()))
			.then(() => {
				(<ConnectionSection>this.parent).list();
			});
	}

	public serializeValidateConnection(): IValidateConnection {
		let validateConnection: IValidateConnection = {
			id: null,
			dataSource: this.dataSource(),
			initialCatalog: this.initialCatalog(),
			password: this.password(),
			providerType: this.provider.getSelectedItem().id,
			userId: this.userId(),
			port: this.port()
		};
		return validateConnection;
	}

	public serialize(): ISaveConnection {
		let connection: ISaveConnection = {
			dataSource: this.dataSource(),
			initialCatalog: this.initialCatalog(),
			password: this.password(),
			providerType: this.provider.getSelectedItem().id,
			userId: this.userId(),
			port: this.port()
		};
		return connection;
	}
	public reset(): void {
		this.notification.hide();
		this.provider.clearSelection();
		this.provider.selectBy(DbProviderType.Oracle);
		Util.setValue(
			null,
			this.dataSource,
			this.initialCatalog,
			this.port,
			this.userId,
			this.password
		);
		this.form.hideValidationMessages();
	}

	private showConnectionItems(connectionOption: IOption<DbProviderType>) {
		switch (connectionOption.id) {
			case DbProviderType.Oracle:
				this.dataSourceInput.label.change(Localization.Studio.Connection.Datasource);
				this.dataSourceInput.setSize(Size._1_1);
				this.databaseActive(false);
				this.portActive(false);
				break;
			case DbProviderType.SqlServer:
				this.dataSourceInput.label.change(Localization.Studio.Connection.Datasource);
				this.dataSourceInput.setSize(Size._1_2);
				this.databaseInput.label.change(Localization.Studio.Connection.InitialCatalog);
				this.databaseActive(true);
				this.portActive(false);
				break;
			case DbProviderType.PostgreSql:
				this.dataSourceInput.label.change(Localization.Studio.Connection.Host);
				this.dataSourceInput.setSize(Size._3_8);
				this.databaseInput.label.change(Localization.Studio.Connection.Database);
				this.databaseActive(true);
				this.portActive(true);
				break;
			case DbProviderType.Hana:
				this.dataSourceInput.label.change(Localization.Studio.Connection.Server);
				this.dataSourceInput.setSize(Size._1_2);
				this.databaseInput.label.change(Localization.Studio.Connection.Database);
				this.databaseActive(true);
				this.portActive(false);
				break;
		}
	}
}
